import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Camera,
  ScrollText,
  Receipt as ReceiptIcon,
  Banknote,
  FileSpreadsheet,
  TrendingUp,
  Printer,
  Link as LinkIcon,
  Code2,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Mail,
} from "lucide-react";

const sections = [
  { id: "getting-started", label: "Getting Started", icon: Sparkles },
  { id: "smart-capture", label: "Smart Capture", icon: Camera },
  { id: "bills", label: "Bills", icon: ScrollText },
  { id: "receipts", label: "Receipts & BAS Export", icon: ReceiptIcon },
  { id: "allocate", label: "Bank Allocation", icon: Banknote },
  { id: "quotes", label: "Quotes → 3 Incomes", icon: FileSpreadsheet },
  { id: "income", label: "Income Tab", icon: TrendingUp },
  { id: "weekly", label: "Weekly & Forecast", icon: Printer },
  { id: "paylinks", label: "Public Pay Links", icon: LinkIcon },
  { id: "email", label: "Emailing Bills / Receipts", icon: Mail },
  { id: "embed", label: "Embed on GoDaddy", icon: Code2 },
  { id: "tips", label: "Power-User Tips", icon: CheckCircle2 },
];

const Guide = () => {
  const [active, setActive] = useState("getting-started");

  useEffect(() => {
    const onScroll = () => {
      const offsets = sections.map((s) => {
        const el = document.getElementById(s.id);
        if (!el) return { id: s.id, top: Infinity };
        return { id: s.id, top: Math.abs(el.getBoundingClientRect().top - 120) };
      });
      offsets.sort((a, b) => a.top - b.top);
      setActive(offsets[0].id);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div data-testid="user-guide" className="fade-rise">
      {/* Hero */}
      <div className="swiss-label mb-3 inline-flex items-center gap-2">
        <BookOpen className="h-3 w-3" /> User Guide
      </div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
        Everything Billy does
      </h1>
      <p className="text-neutral-600 mt-3 max-w-3xl">
        A function-by-function walkthrough of the app — what each tab is for,
        how to use it, the AI shortcuts, and a few power-user tips you might
        miss otherwise. Skim the menu on the left or read top-to-bottom.
      </p>

      <div className="mt-6">
        <a
          data-testid="open-manual-link"
          href="/manual"
          className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
        >
          <BookOpen className="h-4 w-4" /> Open printable Training Manual (14 chapters)
        </a>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
        {/* Sticky nav */}
        <nav
          data-testid="guide-nav"
          className="lg:sticky lg:top-6 self-start border-2 border-black bg-white p-4"
        >
          <div className="swiss-label mb-3">Jump to</div>
          <ul className="space-y-1">
            {sections.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  data-testid={`guide-link-${s.id}`}
                  className={`flex items-center gap-2 px-2 py-2 text-[12px] uppercase tracking-[0.12em] font-bold border-l-2 transition-colors ${
                    active === s.id
                      ? "border-black text-black bg-neutral-100"
                      : "border-transparent text-neutral-500 hover:text-black hover:border-black"
                  }`}
                >
                  <s.icon className="h-3.5 w-3.5" />
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Content */}
        <article className="space-y-16 pb-20" data-testid="guide-content">

          <Section id="getting-started" title="Getting started" icon={Sparkles}>
            <p>
              <strong>Billy</strong> is the cash-flow command centre for The
              Outdoor Blind Company. The app lives on your phone, tablet, or
              desktop — your data is stored in the cloud so it's the same
              everywhere.
            </p>
            <Steps
              steps={[
                "Open the app on your phone or browser.",
                'Tap the big black SNAP button (top-right of every page, or the camera button on mobile) to start.',
                "Snap a bill, receipt, quote, or bank transaction screenshot — or upload a PDF / multiple files at once.",
                "Billy's AI reads it, decides what it is, and fills the right form.",
                "Review the fields, tap Save. You're done.",
              ]}
            />
            <Callout>
              <strong>Tip:</strong> Every tab has a different primary action,
              but <strong>SNAP</strong> always opens Smart Capture — it's the
              fastest way to add anything.
            </Callout>
          </Section>

          <Section id="smart-capture" title="Smart Capture (/snap)" icon={Camera}>
            <p>
              The one-tap entry point. Billy looks at the photo / PDF and
              picks one of:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Bill</strong> — an unpaid invoice or statement
              </li>
              <li>
                <strong>Receipt</strong> — proof of a completed purchase
              </li>
              <li>
                <strong>Bank Transaction</strong> — a bank-app or statement
                screenshot you want to allocate
              </li>
              <li>
                <strong>Quote</strong> — a job quote that should become 3
                deposit incomes
              </li>
            </ul>
            <p>
              You'll see <em>Billy's call</em> with a confidence percentage.
              If it got it wrong, just tap the right type — Billy switches
              forms instantly. Save with one tap.
            </p>
            <Steps
              steps={[
                "Tap SNAP (header) or the camera FAB (mobile).",
                'Choose "Take Photo" or "Upload Files / PDF".',
                "You can pick MULTIPLE files at once — or a single multi-page PDF.",
                "Wait 5–15 seconds for the AI to read it (multi-page takes longer).",
                "Review Billy's call, override the type if needed, tap Save.",
              ]}
            />
            <Callout>
              <strong>Multi-page PDFs:</strong> All pages are read together
              (up to 25 pages). Great for long supplier invoices with bank
              details on page 2 or 3.
            </Callout>
          </Section>

          <Section id="bills" title="Bills" icon={ScrollText}>
            <p>
              The Dashboard / Bills tab lists every bill, sorted by due date
              with overdue ones flagged red. Click a bill card for the full
              detail page.
            </p>
            <H3>Bill card actions</H3>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Copy Pay Link</strong> — copies an SMS-ready message
                with the public pay URL. Paste into a text to the customer.
              </li>
              <li>
                <strong>Email Bill</strong> — opens your mail app with a
                pre-filled reminder.
              </li>
              <li>
                <strong>Email Receipt</strong> — appears after a payment is
                recorded; emails the customer their receipt.
              </li>
            </ul>
            <H3>On the bill detail page</H3>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Edit every field including bank details, BPAY, PayID and
                description.
              </li>
              <li>
                Add a payment — supports <strong>partial payments</strong>{" "}
                (e.g. $50 today, $120 next week). Status auto-updates.
              </li>
              <li>
                Set up a <strong>recurring schedule</strong> (weekly / monthly /
                custom).
              </li>
              <li>
                Print a receipt for the customer or save it as PDF.
              </li>
              <li>
                Attach a <strong>bank receipt file</strong> as proof of
                payment.
              </li>
            </ul>
            <Callout>
              <strong>Duplicate protection:</strong> Billy blocks you from
              saving the same invoice number from the same vendor twice.
            </Callout>
          </Section>

          <Section
            id="receipts"
            title="Receipts (& BAS export)"
            icon={ReceiptIcon}
          >
            <p>
              The Receipts tab tracks every till receipt — materials, fuel,
              tools, subbies. The two KPI tiles at the top show{" "}
              <strong>This month's spend</strong> and <strong>all-time
              spend</strong>.
            </p>
            <H3>Categories</H3>
            <p className="text-sm text-neutral-700">
              Materials · Tools · Fuel · Vehicle · Office · Subcontractor ·
              Meals · Other
            </p>
            <H3>BAS export</H3>
            <Steps
              steps={[
                'Tap "Export BAS" (top-right of the Receipts tab).',
                'Pick a preset (This quarter, Last quarter, This FY, Last FY) or set a custom from/to date.',
                'Hit "Download CSV" to grab an Excel-ready file for your accountant.',
                'Or hit "Open print summary" for a print-ready A4 page with totals + GST + category breakdown + every line item.',
                "Browser → Print → Save as PDF to attach to email.",
              ]}
            />
          </Section>

          <Section
            id="allocate"
            title="Bank-Transaction Allocation (/allocate)"
            icon={Banknote}
          >
            <p>
              When you've just paid a bill from the bank app, snap a
              screenshot of the transaction. Billy will:
            </p>
            <ol className="list-decimal pl-6 space-y-1">
              <li>Read the amount, date, reference and counterparty.</li>
              <li>Rank every outstanding bill by how likely it matches.</li>
              <li>Pre-select the best match with the right amount.</li>
            </ol>
            <H3>Splitting one payment across multiple bills</H3>
            <p>
              Hit <strong>"Add another bill"</strong> to add more allocation
              lines. Each line picks a bill from the dropdown (★ marks
              confident matches) and a dollar amount. The{" "}
              <strong>Remaining</strong> badge at the top updates live —
              green when fully allocated, red if you over-allocate.
            </p>
            <Callout>
              <strong>Escape hatches:</strong> You can also save the
              transaction as a brand-new Bill (use-case: a debit you weren't
              expecting) or as a Receipt (a one-off purchase).
            </Callout>
          </Section>

          <Section
            id="quotes"
            title="Quotes → 3 Deposit Incomes (/income/from-quote)"
            icon={FileSpreadsheet}
          >
            <p>
              Outdoor Blind quotes are paid in 3 stages:{" "}
              <strong>50% on signing</strong>, <strong>30% on install</strong>,{" "}
              <strong>20% on completion</strong>. Billy creates all three at
              once.
            </p>
            <Steps
              steps={[
                'On the Income tab, tap "Upload Quote → 3 Incomes" (or SNAP a quote photo / PDF and pick the Quote type).',
                "Billy reads the customer name, quote number, total and install date.",
                "Review the live 3-stage preview on the right — amounts and dates are computed instantly.",
                'Tap "Create 3 Incomes". Three records appear on the Income tab, all tagged with the customer name + quote number.',
              ]}
            />
            <Callout>
              <strong>Dates:</strong> Stage 1 = today (or quote date). Stage 2
              = install date (or +14 days if not on the quote). Stage 3 =
              install date + 14 days.
            </Callout>
          </Section>

          <Section id="income" title="Income Tab" icon={TrendingUp}>
            <p>
              Track every dollar coming in: install deposits, service calls,
              factory repairs, anything. Add an income manually with the{" "}
              <strong>+ Add Income</strong> button, or use the quote-to-3-incomes
              flow above.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Tick "received" to mark money in the door.</li>
              <li>
                Filter by <em>Pending</em> / <em>Received</em> / <em>All</em>.
              </li>
              <li>
                Edit any row in place — customer, category, amount, expected
                date.
              </li>
            </ul>
          </Section>

          <Section
            id="weekly"
            title="Weekly Dashboard & 3-Month Forecast"
            icon={Printer}
          >
            <H3>Weekly tab</H3>
            <p>
              At-a-glance KPIs for the current week: <strong>Going Out</strong>,{" "}
              <strong>Overdue</strong>, <strong>Coming In (expected)</strong>,{" "}
              <strong>Received</strong>, and how close you are to your weekly{" "}
              <strong>profit target</strong> (editable). The bar chart shows the
              last 8 weeks for context.
            </p>
            <H3>3-month forecast (Print 3-Mo Forecast)</H3>
            <p>
              The <strong>PRINT 3-MO FORECAST</strong> button (top-right of
              Weekly) opens a print-ready bank-loan style page showing:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Last 3 months IN / OUT (actuals)</li>
              <li>Next 3 months IN / OUT (projected)</li>
              <li>Historical net, forecast net, overdue total</li>
              <li>Monthly cash-flow table with a running balance</li>
              <li>
                Itemised lists of every expected income and bill due in the
                next 3 months
              </li>
              <li>An overdue-bills section in red, if any</li>
            </ul>
            <Callout>
              <strong>For loan applications:</strong> Browser → Print → Save as
              PDF. The page has print-specific CSS that strips the app chrome
              for a clean A4 document.
            </Callout>
          </Section>

          <Section id="paylinks" title="Public Pay Links (/pay/:id)" icon={LinkIcon}>
            <p>
              Every bill has a unique URL the customer can open without
              logging in. They see what they owe and tap once to mark it paid
              — which updates your dashboard live.
            </p>
            <Steps
              steps={[
                'On a bill card, tap "Copy Pay Link".',
                "Open Messages, paste, send to the customer.",
                "They tap the link, see the amount and pay options, tap Mark as Paid.",
                "Billy updates the bill status to Paid (or Part-Paid for a partial amount) instantly.",
              ]}
            />
          </Section>

          <Section id="email" title="Emailing Bills & Receipts" icon={Mail}>
            <p>
              Billy uses your phone or computer's <strong>default mail app</strong>{" "}
              (Outlook, Gmail, Apple Mail) to compose emails. The bill / receipt
              email opens with the recipient, subject and a full body
              pre-filled — review, tweak if needed, hit Send.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Add a customer email address on the bill detail page first —
                otherwise the email opens with no recipient.
              </li>
              <li>
                The Email Bill template includes the pay-link URL and amount.
              </li>
              <li>
                The Email Receipt template confirms what was paid and when.
              </li>
            </ul>
            <Callout>
              <strong>Heads-up:</strong> Because Billy uses your mail app, you
              must have it set up and logged in. Auto-sending without your mail
              app needs a Resend or SMTP integration — flag if you want it.
            </Callout>
          </Section>

          <Section id="embed" title="Embed on GoDaddy" icon={Code2}>
            <p>
              The Embed tab gives you copy-paste HTML to drop Billy into your
              GoDaddy website. Two options:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Simple iframe</strong> — fixed height, good for a
                desktop-first admin page.
              </li>
              <li>
                <strong>Responsive iframe</strong> — adjusts to the device,
                ideal for mobile.
              </li>
            </ul>
            <Steps
              steps={[
                "Open the Embed tab, click Copy on whichever snippet you want.",
                "GoDaddy → Website Builder → edit the page where Billy should live.",
                'Add a "Custom HTML" / "Embed" section and paste.',
                "Publish. Billy now runs inside your website.",
              ]}
            />
          </Section>

          <Section id="tips" title="Power-User Tips" icon={CheckCircle2}>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Snap on the drive home.</strong> Smart Capture handles
                multiple photos at once — grab the day's supplier bills in one
                hit.
              </li>
              <li>
                <strong>Use the pay link as a text.</strong> Tradies who text
                links get paid 2–3× faster than tradies who chase by phone.
              </li>
              <li>
                <strong>Forecast before Friday.</strong> The Weekly tab tells
                you on Tuesday whether the week's headed under target — chase
                early, not late.
              </li>
              <li>
                <strong>BAS export quarterly.</strong> The CSV is in the exact
                shape an accountant wants. Set a calendar reminder for the 1st
                of Jan / Apr / Jul / Oct.
              </li>
              <li>
                <strong>Override the AI without guilt.</strong> If Smart
                Capture mis-classifies, just tap the right type — the
                extracted fields stay; only the form changes.
              </li>
              <li>
                <strong>Multi-bill bank transfer?</strong> Use Allocate's
                split feature so a single $1,200 EFT clears three different
                supplier invoices at once.
              </li>
            </ul>
            <div className="mt-8 border-2 border-black p-5 bg-black text-white">
              <div className="font-display font-extrabold text-xl">
                Ready when you are.
              </div>
              <p className="mt-2 text-sm text-neutral-300">
                Most things in Billy are a tap away. Start by snapping
                something — Billy figures out the rest.
              </p>
              <Link
                to="/snap"
                data-testid="guide-snap-cta"
                className="mt-5 inline-flex items-center gap-2 bg-white text-black px-4 py-3 font-bold uppercase tracking-widest text-xs hover:bg-red-600 hover:text-white transition-colors"
              >
                <Camera className="h-4 w-4" /> Open Smart Capture
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Section>

        </article>
      </div>
    </div>
  );
};

const Section = ({ id, title, icon: Icon, children }) => (
  <section id={id} className="scroll-mt-24" data-testid={`section-${id}`}>
    <div className="flex items-center gap-3 border-b-2 border-black pb-3 mb-5">
      <Icon className="h-5 w-5" />
      <h2 className="font-display font-extrabold text-2xl tracking-tight uppercase">
        {title}
      </h2>
    </div>
    <div className="prose-billy space-y-4 text-[15px] leading-relaxed text-neutral-800">
      {children}
    </div>
  </section>
);

const H3 = ({ children }) => (
  <h3 className="font-display font-bold text-[15px] uppercase tracking-widest mt-6 mb-2">
    {children}
  </h3>
);

const Steps = ({ steps }) => (
  <ol className="space-y-2 mt-2">
    {steps.map((s, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="flex-none h-6 w-6 border-2 border-black bg-white text-black font-bold text-[12px] flex items-center justify-center mono">
          {i + 1}
        </span>
        <span className="text-[15px] leading-relaxed">{s}</span>
      </li>
    ))}
  </ol>
);

const Callout = ({ children }) => (
  <div className="border-l-4 border-black bg-neutral-50 p-4 text-[14px] leading-relaxed">
    {children}
  </div>
);

export default Guide;
